/**
 * VVorms shared design system ("Fusion" identity).
 *
 * One source of truth for the visual language used across every surface
 * (title, lobby, in-game HUD): GunBound's cohesive glossy arcade chrome
 * (beveled blue/gold panels, LCD accents) fused with Worms Armageddon's
 * chunky hand-drawn cartoon charm (heavy display type, toy-like buttons).
 *
 * The HTML surfaces consume the CSS custom properties + component classes
 * below; the Pixi HUD mirrors the same tokens in `src/ui/theme.ts`. Keep the
 * two in lockstep when changing the palette.
 */

export const DESIGN_SYSTEM_CSS = `
  @font-face {
    font-family: 'Lilita One';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/lilita-one-latin.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Fredoka';
    font-style: normal;
    font-weight: 500 700;
    font-display: swap;
    src: url('/fonts/fredoka-latin.woff2') format('woff2');
  }

  :root {
    /* Surface / chrome (GunBound glossy blue cockpit) */
    --vv-bg: #0c1830;
    --vv-bg-2: #122444;
    --vv-panel-top: #21407a;
    --vv-panel-bottom: #0e2046;
    --vv-bevel-light: #5288d6;
    --vv-bevel-dark: #060f22;
    --vv-stroke: #2a4d86;

    /* Accents */
    --vv-gold: #ffc63b;
    --vv-gold-deep: #e89a1c;
    --vv-go: #46c84a;
    --vv-go-deep: #2f9b35;
    --vv-danger: #ec4438;
    --vv-danger-deep: #b32a20;
    --vv-cyan: #38c6e0;
    --vv-blue: #3a7bd5;
    --vv-purple: #a463d8;

    /* Text */
    --vv-text: #ffffff;
    --vv-text-dim: #aebbd6;
    --vv-ink: #08152e;

    /* Type */
    --vv-font-display: 'Lilita One', system-ui, 'Arial Black', sans-serif;
    --vv-font-ui: 'Fredoka', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

    /* Spacing scale (8pt) */
    --vv-s1: 4px;  --vv-s2: 8px;  --vv-s3: 12px;
    --vv-s4: 16px; --vv-s5: 24px; --vv-s6: 32px;

    /* Z-scale (per game-ui-design: dropdowns<modals<tooltips<notifications) */
    --vv-z-panel: 10;
    --vv-z-modal: 200;
    --vv-z-tooltip: 300;
    --vv-z-toast: 400;
  }

  /* ---- Background ------------------------------------------------------ */
  /* Sky-to-night radial + a soft "battlefield horizon" glow, all painted as
     element backgrounds so they always sit behind content (no pseudo-element
     stacking surprises). */
  .vv-bg {
    background:
      radial-gradient(120% 65% at 50% 122%, rgba(122,74,40,0.5) 0%, rgba(122,74,40,0) 55%),
      radial-gradient(85% 55% at 50% 120%, rgba(91,178,46,0.35) 0%, rgba(91,178,46,0) 60%),
      radial-gradient(130% 90% at 50% -10%, #2c5187 0%, #15294f 48%, #0a1428 100%);
  }

  /* ---- Panels (beveled glossy chrome) --------------------------------- */
  .vv-panel {
    background: linear-gradient(180deg, var(--vv-panel-top), var(--vv-panel-bottom));
    border-radius: 14px;
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.18),
      inset 0 0 0 2px rgba(82,136,214,0.35),
      inset 0 -4px 10px rgba(0,0,0,0.35),
      0 6px 18px rgba(0,0,0,0.45);
    position: relative;
  }
  .vv-panel--gold {
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.25),
      inset 0 0 0 2px rgba(255,198,59,0.55),
      inset 0 -4px 10px rgba(0,0,0,0.35),
      0 6px 18px rgba(0,0,0,0.45);
  }

  /* ---- Buttons (toy-like 3D, GunBound gloss + Worms chunk) ------------- */
  .vv-btn {
    font-family: var(--vv-font-ui);
    font-weight: 700;
    font-size: clamp(16px, 3.6vw, 19px);
    color: #fff;
    border: none;
    border-radius: 12px;
    padding: 15px 22px;
    min-height: 48px;
    cursor: pointer;
    position: relative;
    letter-spacing: 0.3px;
    text-shadow: 0 2px 0 rgba(0,0,0,0.32);
    background: linear-gradient(180deg, var(--btn-1, #5b9be8), var(--btn-2, #2f63b8));
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.45),
      inset 0 -3px 0 rgba(0,0,0,0.28),
      0 4px 0 var(--btn-edge, #1d447f),
      0 6px 12px rgba(0,0,0,0.4);
    transition: transform 0.1s ease, filter 0.1s ease, box-shadow 0.1s ease;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .vv-btn:not(:disabled):hover { filter: brightness(1.08); }
  .vv-btn:not(:disabled):active {
    transform: translateY(3px);
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.3),
      inset 0 -2px 0 rgba(0,0,0,0.3),
      0 1px 0 var(--btn-edge, #1d447f),
      0 2px 6px rgba(0,0,0,0.4);
  }
  .vv-btn:focus-visible { outline: 3px solid var(--vv-cyan); outline-offset: 3px; }
  .vv-btn:disabled { opacity: 0.45; cursor: not-allowed; filter: grayscale(0.4); }

  .vv-btn--primary { --btn-1:#5fd463; --btn-2:#2f9b35; --btn-edge:#1d6b22; }
  .vv-btn--gold    { --btn-1:#ffd860; --btn-2:#e89a1c; --btn-edge:#a96a0e;
                     color:#3a2400; text-shadow:0 1px 0 rgba(255,255,255,0.45); }
  .vv-btn--secondary { --btn-1:#5b9be8; --btn-2:#2f63b8; --btn-edge:#1d447f; }
  .vv-btn--danger  { --btn-1:#f4675c; --btn-2:#c4382c; --btn-edge:#8c241b; }
  .vv-btn--ghost   { --btn-1:#33446b; --btn-2:#212f4f; --btn-edge:#141d33; }

  /* ---- Display title (chunky cartoon, gold fill + brown outline) ------- */
  .vv-title {
    font-family: var(--vv-font-display);
    font-weight: 400;
    line-height: 0.92;
    letter-spacing: 2px;
    color: #ffd34d;
    -webkit-text-stroke: 2px #5a2a06;
    paint-order: stroke fill;
    text-shadow:
      0 -1px 0 #fff6cf,
      3px 4px 0 #a8530c,
      6px 8px 14px rgba(0,0,0,0.5);
  }

  /* ---- Inputs --------------------------------------------------------- */
  .vv-input {
    font-family: var(--vv-font-ui);
    font-weight: 600;
    background: rgba(8,16,34,0.6);
    border: 2px solid var(--vv-stroke);
    border-radius: 10px;
    color: var(--vv-text);
    padding: 12px 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    -webkit-appearance: none;
  }
  .vv-input:focus {
    outline: none;
    border-color: var(--vv-gold);
    box-shadow: 0 0 0 3px rgba(255,198,59,0.25);
  }

  /* ---- Mobile virtual gamepad: share the UI font ---------------------- */
  .gamepad-dpad, .gamepad-buttons, .gamepad-top-buttons, .btn-shoot-drag {
    font-family: var(--vv-font-ui);
  }

  /* ---- Accessibility: respect reduced motion -------------------------- */
  @media (prefers-reduced-motion: reduce) {
    .vv-btn, .vv-input { transition: none; }
  }
`;

let installed = false;

/** Inject the design-system stylesheet once (fonts + tokens + components). */
export function installDesignSystem(): void {
  if (installed || typeof document === 'undefined') return;
  if (document.getElementById('vv-design-system')) {
    installed = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'vv-design-system';
  style.textContent = DESIGN_SYSTEM_CSS;
  document.head.appendChild(style);
  installed = true;
}

/**
 * Ensure the custom fonts are rasterised before Pixi measures/draws text
 * (otherwise the first HUD frame falls back to a system font and never
 * repaints). Safe to call repeatedly; resolves immediately when unsupported.
 */
export async function ensureFontsReady(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load("400 24px 'Lilita One'"),
      document.fonts.load("600 16px 'Fredoka'"),
      document.fonts.load("700 16px 'Fredoka'"),
    ]);
    await document.fonts.ready;
  } catch {
    /* font loading is best-effort */
  }
}
